import { useState } from "react";
import { invoke } from "../lib/bridge";
import { WaitingForApproval } from "./WaitingForApproval";
import type { FamilyInfo, JoinResult } from "../lib/onboardingTypes";

export const INVITE_CODE_LENGTH = 12;

interface InvitePreview {
  familyName: string;
  members: { id: number; displayName: string }[];
  expiresAt: string;
  deviceLink: boolean;
  linkMemberName: string | null;
}

interface JoinWithCodeProps {
  onJoined: (info: FamilyInfo) => void;
  onCancel: () => void;
}

export function JoinWithCode({ onJoined, onCancel }: JoinWithCodeProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [memberName, setMemberName] = useState("");
  const [joining, setJoining] = useState(false);
  const [pendingJoin, setPendingJoin] = useState<JoinResult | null>(null);

  const handleLookup = async () => {
    if (code.trim().length < INVITE_CODE_LENGTH) return;
    setLoading(true);
    setError("");
    try {
      const result = await invoke<InvitePreview>("redeem_invite_code", {
        code: code.trim(),
      });
      setPreview(result);
    } catch (e) {
      setError(String(e));
    }
    setLoading(false);
  };

  const handleJoin = async (name: string) => {
    setJoining(true);
    setError("");
    try {
      const result = await invoke<JoinResult>("join_family", {
        code: code.trim(),
        memberName: name,
      });
      setPendingJoin(result);
    } catch (e) {
      setError(String(e));
      setJoining(false);
    }
  };

  if (pendingJoin) {
    return (
      <WaitingForApproval
        joinRequestId={pendingJoin.joinRequestId}
        familyName={pendingJoin.familyName}
        deviceLink={preview?.deviceLink ?? false}
        onApproved={onJoined}
        onRejected={onCancel}
      />
    );
  }

  if (preview?.deviceLink) {
    return (
      <main className="container">
        <div className="header-row">
          <h1 className="header-title">Link this device</h1>
          <button onClick={onCancel} className="btn-close" aria-label="Close">&times;</button>
        </div>
        <p style={{ textAlign: "center", color: "var(--color-hint)", marginBottom: "24px" }}>
          Add this device to <strong>{preview.familyName}</strong>
          {preview.linkMemberName ? <> as <strong>{preview.linkMemberName}</strong></> : null}. Your
          existing data will sync here.
        </p>
        {error && <p style={{ color: "var(--color-error)", textAlign: "center", marginBottom: "16px" }}>{error}</p>}
        <button
          onClick={() => handleJoin("")}
          disabled={joining}
          className="btn-primary btn-full"
        >
          {joining ? "Linking..." : "Link this device"}
        </button>
        <button
          onClick={() => { setPreview(null); setError(""); }}
          className="btn-secondary btn-full"
          style={{ marginTop: "12px" }}
        >
          Back
        </button>
      </main>
    );
  }

  if (preview) {
    return (
      <main className="container">
        <div className="header-row">
          <h1 className="header-title">Join {preview.familyName}?</h1>
          <button onClick={onCancel} className="btn-close" aria-label="Close">&times;</button>
        </div>
        <p style={{ textAlign: "center", color: "var(--color-hint)", marginBottom: "16px" }}>
          {preview.members.length} member{preview.members.length !== 1 ? "s" : ""}:
          {" " + preview.members.map(m => m.displayName).join(", ")}
        </p>
        <div style={{ marginBottom: "24px" }}>
          <label className="field-label" htmlFor="join-code-member-name">What's your name?</label>
          <input
            id="join-code-member-name"
            value={memberName}
            onChange={(e) => setMemberName(e.target.value)}
            placeholder="Your name"
            className="field-input"
          />
        </div>
        {error && <p style={{ color: "var(--color-error)", textAlign: "center", marginBottom: "16px" }}>{error}</p>}
        <button
          onClick={() => memberName.trim() && handleJoin(memberName.trim())}
          disabled={joining || !memberName.trim()}
          className="btn-primary btn-full"
        >
          {joining ? "Joining..." : `Join ${preview.familyName}`}
        </button>
        <button
          onClick={() => { setPreview(null); setError(""); }}
          className="btn-secondary btn-full"
          style={{ marginTop: "12px" }}
        >
          Back
        </button>
      </main>
    );
  }

  return (
    <main className="container">
      <div className="header-row">
        <h1 className="header-title">Join Family</h1>
        <button onClick={onCancel} className="btn-close" aria-label="Close">&times;</button>
      </div>
      <p style={{ textAlign: "center", color: "var(--color-hint)", marginBottom: "24px" }}>
        Enter the {INVITE_CODE_LENGTH}-character invite code
      </p>
      <div style={{ marginBottom: "24px" }}>
        <label className="field-label" htmlFor="join-invite-code">Invite code</label>
        <input
          id="join-invite-code"
          aria-label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, INVITE_CODE_LENGTH))}
          placeholder="ABCDEFGHJKLM"
          className="field-input"
          style={{ textAlign: "center", fontSize: "18px", letterSpacing: "4px", fontFamily: "monospace" }}
          maxLength={INVITE_CODE_LENGTH}
        />
      </div>
      {error && <p style={{ color: "var(--color-error)", textAlign: "center", marginBottom: "16px" }}>{error}</p>}
      <button
        onClick={handleLookup}
        disabled={loading || code.trim().length < INVITE_CODE_LENGTH}
        className="btn-primary btn-full"
      >
        {loading ? "Checking..." : "Next"}
      </button>
    </main>
  );
}
